import React, { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { 
  Plane, 
  Anchor, 
  Car,
  Crown,
  ArrowRight,
  MapPin,
  Users,
  Clock,
  Star,
  Zap,
  Shield,
  Heart,
  MessageCircle
} from 'lucide-react';

const LuxuryTransportPage = () => {
  const [activeService, setActiveService] = useState('jets');

  const services = [
    { id: 'jets', name: 'Jets Privés', icon: Plane },
    { id: 'yachts', name: 'Yachts', icon: Anchor }, 
    { id: 'cars', name: 'Véhicules Premium', icon: Car }
  ];

  const jetFleet = [
    {
      name: "Gulfstream G650ER",
      category: "Ultra Long Range",
      image: "https://images.unsplash.com/photo-1540962351504-03099e0a754b?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      passengers: "14-19 passagers",
      range: "13 900 km",
      speed: "956 km/h",
      price: "À partir de 12 000€/h",
      features: ["Suite présidentielle", "Cuisine gastronomique", "Wifi haut débit", "Système divertissement 4K"]
    },
    {
      name: "Bombardier Global 7500",
      category: "Ultra Long Range",
      image: "https://images.unsplash.com/photo-1583944318146-66e1e22693c6?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      passengers: "12-17 passagers",
      range: "14 260 km",
      speed: "925 km/h", 
      price: "À partir de 11 500€/h",
      features: ["4 espaces distincts", "Chambre complète", "Douche privée", "Bar premium"]
    },
    {
      name: "Citation CJ4",
      category: "Light Jet",
      image: "https://images.unsplash.com/photo-1556388158-158ea5ccacbd?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      passengers: "6-8 passagers",
      range: "3 408 km",
      speed: "779 km/h",
      price: "À partir de 4 500€/h",
      features: ["Idéal courtes distances", "Décollage court", "Intérieur raffiné", "Service personnalisé"]
    }
  ];

  const yachtFleet = [
    {
      name: "Superyacht Serenity",
      category: "165 pieds",
      image: "https://images.unsplash.com/photo-1544551763-46a013bb70d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      passengers: "12 invités",
      crew: "8 membres d'équipage",
      amenities: "Hélipad, spa, cinéma",
      price: "À partir de 45 000€/semaine",
      features: ["5 suites luxueuses", "Spa et jacuzzi", "Hélipad privé", "Chef étoilé à bord"]
    },
    {
      name: "Yacht Prestige",
      category: "130 pieds",
      image: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      passengers: "10 invités",
      crew: "6 membres d'équipage",
      amenities: "Piscine, gym, terrasse",
      price: "À partir de 32 000€/semaine",
      features: ["4 cabines master", "Piscine à débordement", "Jet skis inclus", "Service majordome"]
    },
    {
      name: "Sailing Yacht Elite",
      category: "120 pieds",
      image: "https://images.unsplash.com/photo-1558618138-1c611b1ad428?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      passengers: "8 invités", 
      crew: "5 membres d'équipage",
      amenities: "Voiles high-tech, bar",
      price: "À partir de 28 000€/semaine",
      features: ["Navigation écologique", "Pont spacieux", "Bar panoramique", "Équipement sports nautiques"]
    }
  ];

  const carFleet = [
    {
      name: "Rolls-Royce Phantom",
      category: "Limousine Ultra-Luxe",
      image: "https://images.unsplash.com/photo-1605559424843-9e4c228bf1c2?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      passengers: "4 passagers",
      features: "Chauffeur privé, champagne",
      range: "Transferts VIP",
      price: "À partir de 350€/h",
      amenities: ["Sièges massage", "Bar intégré", "Cloison électrique", "Système audio premium"]
    },
    {
      name: "Bentley Bentayga",
      category: "SUV Premium",
      image: "https://images.unsplash.com/photo-1493238792000-8113da705763?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      passengers: "5 passagers",
      features: "Tout-terrain luxe",
      range: "Excursions",
      price: "À partir de 280€/h",
      amenities: ["Intérieur cuir", "Système navigation", "Climatisation zones", "Coffre spacieux"]
    },
    {
      name: "Mercedes-Benz Classe S",
      category: "Berline Executive",
      image: "https://images.unsplash.com/photo-1617788138017-80ad40651399?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      passengers: "4 passagers",
      features: "Confort absolu",
      range: "Transfers aéroport",
      price: "À partir de 200€/h",
      amenities: ["Sièges inclinables", "Wifi à bord", "Écrans individuels", "Minibar"]
    }
  ];

  const getCurrentFleet = () => {
    switch(activeService) {
      case 'jets': return jetFleet;
      case 'yachts': return yachtFleet;
      case 'cars': return carFleet;
      default: return jetFleet;
    }
  };

  const benefits = [
    {
      icon: Clock,
      title: "Disponibilité 24/7",
      description: "Réservation et départ à tout moment selon vos besoins"
    },
    {
      icon: Shield,
      title: "Sécurité Maximale",
      description: "Flotte certifiée, équipages expérimentés, maintenance rigoureuse"
    },
    {
      icon: Crown,
      title: "Service VIP",
      description: "Conciergerie dédiée, personnalisation complète du voyage"
    },
    {
      icon: Zap,
      title: "Efficacité Absolue",
      description: "Gain de temps considérable, pas d'attente, horaires flexibles"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Hero Section */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1540962351504-03099e0a754b?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70" />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center text-white">
          <Badge 
            variant="secondary" 
            className="mb-6 px-6 py-3 bg-white/10 backdrop-blur-md border-white/20 text-white text-sm font-light uppercase tracking-wider"
          >
            <Plane className="w-4 h-4 mr-2" />
            Transport d'Exception
          </Badge>
          
          <h1 
            className="text-5xl md:text-6xl font-light mb-6 tracking-wide"
            style={{ 
              fontFamily: 'Playfair Display, serif',
              textShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
            }}
          >
            Yacht & Jets Privés
          </h1>
          
          <p 
            className="text-xl md:text-2xl font-light mb-12 opacity-90 max-w-3xl mx-auto leading-relaxed"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Voyagez selon vos propres règles avec notre flotte d'exception : jets privés, superyachts et véhicules de prestige
          </p>

          <Button 
            size="lg"
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-4 rounded-full font-medium text-lg transition-all duration-300 transform hover:scale-105"
          >
            Réserver un Transport
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Service Tabs */}
      <section className="py-12 px-6 bg-white/50 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-center gap-4">
            {services.map((service) => {
              const IconComponent = service.icon;
              return (
                <Button
                  key={service.id}
                  variant={activeService === service.id ? 'default' : 'outline'}
                  onClick={() => setActiveService(service.id)}
                  className={`px-8 py-4 rounded-full transition-all duration-300 ${
                    activeService === service.id
                      ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg transform -translate-y-1'
                      : 'bg-white/80 text-gray-700 border-gray-200 hover:bg-white/90 backdrop-blur-sm'
                  }`}
                >
                  <IconComponent className="w-5 h-5 mr-2" />
                  {service.name}
                </Button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Fleet Display */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-4xl font-light mb-6 text-gray-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Notre Flotte d'Exception
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Des véhicules de prestige maintenus aux plus hauts standards de qualité et de sécurité
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {getCurrentFleet().map((vehicle, index) => (
              <Card key={index} className="overflow-hidden hover:shadow-2xl transition-all duration-500 group">
                <div className="relative h-56">
                  <img 
                    src={vehicle.image}
                    alt={vehicle.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-white/20 backdrop-blur-md border-white/30 text-white text-xs">
                      {vehicle.category}
                    </Badge>
                  </div>

                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <h3 
                      className="text-xl font-light mb-1"
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      {vehicle.name}
                    </h3>
                    <div className="text-lg font-light text-blue-300">
                      {vehicle.price}
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <div className="space-y-2 mb-6 text-sm text-gray-600">
                    <div className="flex items-center justify-between">
                      <span>Capacité:</span>
                      <span className="font-medium">{vehicle.passengers}</span>
                    </div>
                    {vehicle.range && (
                      <div className="flex items-center justify-between">
                        <span>Portée:</span>
                        <span className="font-medium">{vehicle.range}</span>
                      </div>
                    )}
                    {vehicle.crew && (
                      <div className="flex items-center justify-between">
                        <span>Équipage:</span>
                        <span className="font-medium">{vehicle.crew}</span>
                      </div>
                    )}
                    {vehicle.amenities && (
                      <div className="flex items-center justify-between">
                        <span>Équipements:</span>
                        <span className="font-medium">{vehicle.amenities}</span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2 mb-6">
                    <h4 className="font-medium text-gray-800 text-sm">Prestations incluses :</h4>
                    {vehicle.features.slice(0, 3).map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <Star className="w-3 h-3 text-blue-600" />
                        <span className="text-xs text-gray-600">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <Button 
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white transition-all duration-300"
                  >
                    Réserver
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-6 bg-gradient-to-r from-blue-900 to-indigo-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl font-light mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Pourquoi Choisir PRIVIA Transport
            </h2>
            <p 
              className="text-xl opacity-90 max-w-2xl mx-auto leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Des avantages exclusifs pour un voyage sans contrainte
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center mx-auto mb-6">
                    <IconComponent className="w-8 h-8 text-blue-300" />
                  </div>
                  
                  <h3 className="text-lg font-light mb-4">{benefit.title}</h3>
                  <p className="text-blue-200 text-sm leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Booking Process - Minimalist Luxury Design */}
      <section className="py-24 px-6 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-20">
            <Badge 
              variant="secondary" 
              className="mb-6 px-4 py-2 bg-amber-50 border-amber-200 text-amber-700 text-xs font-light uppercase tracking-widest"
            >
              Excellence Discrète
            </Badge>
            <h2 
              className="text-4xl md:text-5xl font-light mb-8 text-gray-900 tracking-wide"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              L'Art de Voyager
            </h2>
            <p 
              className="text-lg text-gray-600 max-w-2xl mx-auto font-light leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Un processus raffiné où chaque détail révèle notre engagement envers la perfection
            </p>
          </div>

          <div className="space-y-12">
            {[
              { 
                step: "01", 
                title: "Consultation Privée", 
                desc: "Échange confidentiel pour comprendre vos aspirations de voyage",
                icon: MessageCircle,
                detail: "Entretien personnalisé • Analyse de vos besoins • Suggestions expertes"
              },
              { 
                step: "02", 
                title: "Curation d'Excellence", 
                desc: "Sélection méticuleuse des options les plus raffinées",
                icon: Crown,
                detail: "Flotte certifiée • Standards PRIVIA • Vérifications rigoureuses"
              },
              { 
                step: "03", 
                title: "Personnalisation Absolue", 
                desc: "Adaptation de chaque service à votre vision du luxe",
                icon: Zap,
                detail: "Services sur-mesure • Conciergerie dédiée • Attention aux détails"
              }
            ].map((phase, index) => {
              const IconComponent = phase.icon;
              return (
                <div key={index} className="group">
                  <div className="bg-white rounded-2xl p-8 md:p-12 shadow-sm hover:shadow-xl transition-all duration-500 border border-gray-100">
                    <div className="flex flex-col md:flex-row items-start gap-8">
                      {/* Step Indicator */}
                      <div className="flex-shrink-0">
                        <div className="relative">
                          <div className="w-20 h-20 bg-gradient-to-br from-amber-100 to-amber-200 rounded-full flex items-center justify-center group-hover:from-amber-200 group-hover:to-amber-300 transition-all duration-300">
                            <IconComponent className="w-8 h-8 text-amber-700" />
                          </div>
                          <div className="absolute -top-2 -right-2 w-8 h-8 bg-amber-600 rounded-full flex items-center justify-center">
                            <span 
                              className="text-xs font-light text-amber-50 tracking-wider"
                              style={{ fontFamily: 'Inter, sans-serif' }}
                            >
                              {phase.step}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <h3 
                          className="text-2xl md:text-3xl font-light mb-4 text-gray-900 tracking-wide"
                          style={{ fontFamily: 'Playfair Display, serif' }}
                        >
                          {phase.title}
                        </h3>
                        <p 
                          className="text-lg text-gray-600 mb-6 leading-relaxed font-light"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          {phase.desc}
                        </p>
                        <div className="flex items-center gap-3 text-sm text-amber-700">
                          <div className="w-1 h-1 bg-amber-500 rounded-full" />
                          <span className="font-light tracking-wide">{phase.detail}</span>
                        </div>
                      </div>

                      {/* Subtle Arrow for non-last items */}
                      {index < 2 && (
                        <div className="hidden md:block flex-shrink-0">
                          <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-amber-500 transition-colors duration-300" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Trust Elements */}
          <div className="mt-16 pt-12 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div className="space-y-3">
                <div className="w-12 h-12 bg-amber-50 rounded-full flex items-center justify-center mx-auto">
                  <Shield className="w-6 h-6 text-amber-600" />
                </div>
                <h4 
                  className="text-lg font-light text-gray-800"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Confidentialité Absolue
                </h4>
                <p className="text-sm text-gray-600 font-light">Discrétion garantie pour chaque voyage</p>
              </div>
              <div className="space-y-3">
                <div className="w-12 h-12 bg-amber-50 rounded-full flex items-center justify-center mx-auto">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
                <h4 
                  className="text-lg font-light text-gray-800"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Disponibilité Immédiate
                </h4>
                <p className="text-sm text-gray-600 font-light">Réactivité 24/7 pour vos demandes urgentes</p>
              </div>
              <div className="space-y-3">
                <div className="w-12 h-12 bg-amber-50 rounded-full flex items-center justify-center mx-auto">
                  <Heart className="w-6 h-6 text-amber-600" />
                </div>
                <h4 
                  className="text-lg font-light text-gray-800"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Excellence Personnalisée
                </h4>
                <p className="text-sm text-gray-600 font-light">Chaque détail pensé pour votre plaisir</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-4xl font-light mb-6 text-gray-800"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Prêt pour le Voyage Ultime ?
          </h2>
          <p 
            className="text-xl mb-8 text-gray-600 leading-relaxed max-w-2xl mx-auto"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Transformez votre déplacement en expérience extraordinaire avec notre flotte d'exception
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-4 rounded-full font-medium text-lg transition-all duration-300 transform hover:scale-105"
            >
              Réserver Maintenant
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-8 py-4 rounded-full font-medium text-lg transition-all duration-300"
            >
              Demander un Devis
            </Button>
          </div>

          <div className="bg-white rounded-lg p-6 max-w-md mx-auto">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Heart className="w-4 h-4 text-blue-600" />
              <p className="text-sm font-medium text-gray-800">Service 24/7</p>
            </div>
            <p className="text-2xl font-light text-blue-600">+1 786 840 0745</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LuxuryTransportPage;